import React from 'react';
import type { ECharts } from 'echarts/core';
import { Chart, DownloadButton, EmptyState, Panel } from '../../../../ui-kit';
import { downloadChartImage, type ChartImageFormat } from '../../../../utils/chartDownload';
import { formatValue } from '../../format';
import { measureFormat } from '../lookupFormat';
import type { LookupMonthlyBlock } from '../../../../types/lookupDashboard';

/**
 * Records by calendar month, as columns.
 *
 * Every month between the first and last record is drawn, quiet months as
 * gaps at zero, so a route that went silent for a season looks it. Colours
 * come from the chart theme. A month still under way, when the board marks
 * it, is drawn lighter and labelled "to date", so it is not read as a quiet one.
 */
export default function MonthlyPanel({ block, loading }: { block: LookupMonthlyBlock; loading?: boolean }) {
  const { measure, points } = block;
  // The chart in the panel, not the expanded copy, is the one saved.
  const chartRef = React.useRef<ECharts | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const download = (format: ChartImageFormat) => {
    setProblem(null);
    try {
      downloadChartImage(chartRef.current, format, block.title);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'The chart could not be downloaded.');
    }
  };

  const chart = (fill: boolean) => {
    if (!points.length) return <EmptyState message="Nothing flown to chart." height={fill ? undefined : 240} />;
    // Many months crowd the axis; label every nth so the names stay readable.
    const every = Math.max(1, Math.ceil(points.length / 12));
    const partialLabels = new Set(points.filter((p) => p.partial).map((p) => p.label));
    const option = {
      grid: { left: 8, right: 8, top: 24, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v: number) => formatValue(v, measureFormat(measure.format)),
        formatter: (items: { name: string; value: number; marker: string; seriesName: string }[]) => {
          const [item] = items;
          if (!item) return '';
          const name = partialLabels.has(item.name) ? `${item.name}, to date` : item.name;
          return `${name}<br/>${item.marker}${item.seriesName}: <b>${formatValue(item.value, measureFormat(measure.format))}</b>`;
        },
      },
      xAxis: {
        type: 'category',
        data: points.map((p) => p.label),
        axisTick: { show: false },
        axisLabel: {
          interval: every - 1,
          // "May 2025" on two lines: month over year; this month says "to date".
          formatter: (label: string) => (partialLabels.has(label) ? `${label.split(' ')[0]}\nto date` : label.replace(' ', '\n')),
        },
      },
      yAxis: { type: 'value', minInterval: 1, splitNumber: 3 },
      series: [
        {
          name: measure.label,
          type: 'bar',
          barMaxWidth: 28,
          // This month so far is lighter: the same colour, less of it.
          data: points.map((p) => (p.partial ? { value: p.value, itemStyle: { opacity: 0.45 } } : p.value)),
          itemStyle: { borderRadius: [4, 4, 0, 0] },
          label: {
            show: points.length <= 24,
            position: 'top',
            formatter: ({ value }: { value: number }) => (value ? formatValue(value, measureFormat(measure.format)) : ''),
          },
        },
      ],
    };
    return (
      <Chart
        option={option}
        height={fill ? undefined : 240}
        fill={fill}
        onReady={fill ? undefined : (instance) => { chartRef.current = instance; }}
        ariaLabel={`${measure.label} by month, ${points[0].label} to ${points[points.length - 1].label}`}
      />
    );
  };

  return (
    <Panel
      title={block.title}
      subtitle={block.subtitle}
      info={measure.definition ?? undefined}
      // The same Download a chart has in chat.
      actions={
        <DownloadButton
          formats={[
            { key: 'png', label: 'Image (.png)', onDownload: () => download('png') },
            { key: 'svg', label: 'Vector (.svg)', description: 'Sharp at any size', onDownload: () => download('svg') },
          ]}
          disabled={loading || points.length === 0}
        />
      }
      footer={problem}
      loading={loading}
      loadingHeight={240}
      expanded={points.length ? { content: chart(true) } : undefined}
    >
      {chart(false)}
    </Panel>
  );
}
